import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ComponentProps } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { Box, Dialog, IconButton, Snackbar, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import CloseIcon from '@mui/icons-material/Close';
import { PhotoSlider } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import ImageIcon from '@mui/icons-material/Image';
import SubjectIcon from '@mui/icons-material/Subject';
import { getGrampsApiBase, readBearerToken, subscribeToBearerToken } from '../grampsAuth';
import type { GrampsEventMedia } from '../utils';
import { PdfViewer } from './PdfViewer';
import { OcrPanel, OcrZoneOverlay } from './OcrPanel';
import { useMediaOcr, useMediaWithOcr } from '../hooks/useMediaOcr';
import { captureGoal } from '../analytics';

const FULLSCREEN_MEDIA_PARAM = 'fullscreenMedia';

type SliderImage = ComponentProps<typeof PhotoSlider>['images'][number];

/** `withLabel`: under the thumbnail, a line that says what clicking gives
 * ("Lire l’acte" when it has its transcription). */
export function MediaList({ medias, thumbSizePx, withLabel = false }: { medias: GrampsEventMedia[], thumbSizePx?: number, withLabel?: boolean }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [copySnackOpen, setCopySnackOpen] = useState(false);
    const [copySnackMessage, setCopySnackMessage] = useState('Media address copied');
    const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});
    const { treeName } = useParams<{ treeName: string }>();
    const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
    // Transcription panel and zone boxes in the image slider, shown by default
    // and toggled from the slider's toolbar.
    const [showOcr, setShowOcr] = useState(true);
    const [activeZone, setActiveZone] = useState<number | null>(null);

    // Media URLs carry the JWT as a query param (there's no way to attach an
    // Authorization header to an <img>/<video>/<audio> src), so once baked into
    // the DOM they never benefit from the background token-refresh timer in
    // App.tsx the way apiGet/apiPost calls do. Subscribing to token changes
    // (login, logout, the background auto-refresh timer, a 401-triggered
    // refresh) instead of polling means these URLs pick up a fresh JWT the
    // moment one is issued, rather than serving a token that silently expired
    // while the drawer stayed open.
    const token = useSyncExternalStore(subscribeToBearerToken, () => readBearerToken(treeName));

    const grampsApiBase = getGrampsApiBase().replace(/\/+$/, '');
    const apiRootUrl = /^https?:\/\//i.test(grampsApiBase)
        ? new URL(grampsApiBase)
        : new URL(grampsApiBase, window.location.origin);
    const defaultThumbPx = parseInt((import.meta.env.VITE_GRAMPS_MEDIA_THUMB_SIZE as string | undefined)?.trim() || '160', 10);
    const thumbPx = thumbSizePx ?? defaultThumbPx;
    const thumbnailSize = String(thumbPx);

    const isAudioMedia = (item: string) => /\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(item);
    const isVideoMedia = (item: string) => /\.(mp4|mov|m4v|avi|mkv|webm)$/i.test(item);
    const fullscreenKindFor = (file: string): 'audio' | 'video' | 'pdf' | 'image' => {
        if (isAudioMedia(file)) return 'audio';
        if (isVideoMedia(file)) return 'video';
        if (file.endsWith('pdf')) return 'pdf';
        return 'image';
    };
    const imageMedias = useMemo(() => medias.filter(item => !item.file.endsWith('pdf') && !isAudioMedia(item.file) && !isVideoMedia(item.file)), [medias]);
    const pdfMedias = useMemo(() => medias.filter(item => item.file.endsWith('pdf')), [medias]);
    const audioMedias = useMemo(() => medias.filter(item => isAudioMedia(item.file)), [medias]);
    const videoMedias = useMemo(() => medias.filter(item => isVideoMedia(item.file)), [medias]);

    const normalizeHandle = (item: GrampsEventMedia): string | null => {
        if (!item.handle) {
            return null;
        }

        const normalized = item.handle.trim();
        return normalized || null;
    };

    const buildMediaFileUrl = (item: GrampsEventMedia): string | undefined => {
        const rawHandle = normalizeHandle(item);
        if (!rawHandle) {
            return undefined;
        }

        const handle = rawHandle.startsWith('_') ? rawHandle.slice(1) : rawHandle;
        const url = new URL(
            `api/media/${encodeURIComponent(handle)}/file`,
            apiRootUrl,
        );
        if (token) {
            url.searchParams.set('jwt', token);
        }
        return url.toString();
    };

    const buildMediaThumbUrl = (item: GrampsEventMedia, size: string, square: boolean): string | undefined => {
        const rawHandle = normalizeHandle(item);
        if (!rawHandle) {
            return undefined;
        }

        const handle = rawHandle.startsWith('_') ? rawHandle.slice(1) : rawHandle;
        const url = new URL(
            `api/media/${encodeURIComponent(handle)}/thumbnail/${encodeURIComponent(size)}`,
            apiRootUrl,
        );
        if (token) {
            url.searchParams.set('jwt', token);
        }
        url.searchParams.set('square', String(square));
        return url.toString();
    };

    const mediaByFile = useMemo(() => {
        const byFile = new Map<string, GrampsEventMedia>();
        medias.forEach((item) => {
            if (!byFile.has(item.file)) {
                byFile.set(item.file, item);
            }
        });
        return byFile;
    }, [medias]);

    const getMediaAddress = (file: string): string | undefined => {
        const item = mediaByFile.get(file);
        return item ? buildMediaFileUrl(item) : undefined;
    };
    const getThumbAddress = (file: string): string | undefined => {
        const item = mediaByFile.get(file);
        if (!item) {
            return undefined;
        }
        return buildMediaThumbUrl(item, thumbnailSize, true);
    };
    const getPdfThumbAddress = (file: string): string | undefined => getThumbAddress(file);
    const getVideoPosterAddress = (file: string): string | undefined => getThumbAddress(file);

    // Each media item's Gramps handle is globally unique (unlike `file`, which is
    // just a filename and can collide across different scans), so it's what
    // identifies a fullscreen media in the URL and lets many `MediaList`
    // instances on the same page (one per event) each recognize whether the
    // param refers to one of their own items.
    const mediaByHandle = useMemo(() => {
        const byHandle = new Map<string, GrampsEventMedia>();
        medias.forEach((item) => {
            const handle = normalizeHandle(item);
            if (handle && !byHandle.has(handle)) {
                byHandle.set(handle, item);
            }
        });
        return byHandle;
    }, [medias]);

    // The URL query param is the single source of truth for which media (if any
    // of this list's own) is fullscreen, so opening/closing is just navigation:
    // the dialog's content always follows the param, and the back button closes it.
    const fullscreenHandle = searchParams.get(FULLSCREEN_MEDIA_PARAM);
    const fullscreenItem = fullscreenHandle ? mediaByHandle.get(fullscreenHandle) : undefined;
    const activeFullscreenMedia = fullscreenItem ? { kind: fullscreenKindFor(fullscreenItem.file), file: fullscreenItem.file } : null;
    // Images are shown via the `PhotoSlider` lightbox below, not this dialog.
    const dialogMedia = activeFullscreenMedia && activeFullscreenMedia.kind !== 'image' ? activeFullscreenMedia : null;

    const setFullscreenHandle = (handle: string | null, options?: { replace?: boolean }) => {
        const next = new URLSearchParams(searchParams);
        if (handle) {
            next.set(FULLSCREEN_MEDIA_PARAM, handle);
        } else {
            next.delete(FULLSCREEN_MEDIA_PARAM);
        }
        setSearchParams(next, options);
    };
    const openFullscreenMedia = (item: GrampsEventMedia) => {
        const handle = normalizeHandle(item);
        if (!handle || !getMediaAddress(item.file)) {
            return;
        }
        setFullscreenHandle(handle);
    };
    const closeFullscreenMedia = () => setFullscreenHandle(null);

    // Images are shown through `PhotoSlider` (a swipeable, zoomable lightbox)
    // rather than the plain MUI `Dialog` used for pdf/audio/video, so it's kept
    // fully controlled from the same URL param -- swiping to another image
    // updates the param (via `replace`, so it doesn't spam browser history the
    // way opening/closing does), and opening a link straight to an image handle
    // starts the slider at that image.
    // OCR of the image open in the slider (none while no image is open).
    const sliderHandle = activeFullscreenMedia?.kind === 'image' ? fullscreenHandle : null;
    const ocrQuery = useMediaOcr(treeName, sliderHandle);
    const ocrActs = ocrQuery.data?.acts ?? [];
    const ocrPage = ocrActs[0]?.page;
    const imageHandles = useMemo(() => imageMedias.flatMap((item) => item.handle?.trim() || []), [imageMedias]);
    const mediaWithOcr = useMediaWithOcr(treeName, imageHandles);
    // One thumbnail for all the images, the others browsed in the slider: the
    // first one with a transcription, to open on the act that can be read.
    const anyOcr = imageMedias.some((item) => mediaWithOcr.has(normalizeHandle(item) ?? ''));
    const imageCover = imageMedias.find((item) => mediaWithOcr.has(normalizeHandle(item) ?? '')) ?? imageMedias[0];
    // An act seen in the slider, once per image opened: whether it came with
    // its transcription is what tells if visitors reach the transcriptions.
    const reportedActRef = useRef<string | null>(null);
    useEffect(() => {
        if (!sliderHandle) {
            reportedActRef.current = null;
            return;
        }
        if (ocrQuery.isPending || reportedActRef.current === sliderHandle) return;
        reportedActRef.current = sliderHandle;
        captureGoal('act_opened', { media_handle: sliderHandle, has_transcription: ocrActs.length > 0 });
    }, [sliderHandle, ocrQuery.isPending, ocrActs.length]);

    const photoSliderImages = imageMedias.flatMap((item): SliderImage[] => {
        const handle = normalizeHandle(item);
        const src = getMediaAddress(item.file) || getThumbAddress(item.file);
        if (!handle || !src) {
            return [];
        }
        // The open image is rendered with its zone boxes inside the same element,
        // so they zoom and pan with it; its size comes from the OCR result.
        if (showOcr && handle === sliderHandle && ocrPage && ocrPage.zones.length > 0) {
            // A custom render is laid out at exactly this size (no fitting
            // like for plain images), so fit the original into the viewport --
            // on a computer, into the part the side panel leaves free, and
            // shift it there (the slider centres it in the whole viewport).
            const [originalWidth, originalHeight] = ocrPage.original;
            const panelSpace = isMobile ? 0 : Math.min(420, window.innerWidth * 0.38) + 32;
            const fit = Math.min((window.innerWidth - panelSpace) / originalWidth, window.innerHeight / originalHeight, 1);
            const width = Math.round(originalWidth * fit);
            const height = Math.round(originalHeight * fit);
            return [{
                key: handle,
                width,
                height,
                render: ({ attrs }) => (
                    <div {...attrs} style={{ ...attrs.style, position: 'relative', transform: panelSpace ? `translateX(${-panelSpace / 2}px)` : undefined }}>
                        <img src={src} alt="" draggable={false} style={{ width: '100%', height: '100%', display: 'block' }} />
                        <OcrZoneOverlay zones={ocrPage.zones} activeZone={activeZone} onHoverZone={setActiveZone} />
                    </div>
                ),
            }];
        }
        return [{ key: handle, src }];
    });
    const activeImageIndex = activeFullscreenMedia?.kind === 'image'
        ? photoSliderImages.findIndex((image) => image.key === fullscreenHandle)
        : -1;
    const handlePhotoSliderIndexChange = (index: number) => {
        const image = photoSliderImages[index];
        setActiveZone(null);
        if (image) {
            setFullscreenHandle(String(image.key), { replace: true });
        }
    };

    const isImageLoaded = (file: string) => Boolean(loadedImages[file]);
    const markImageAsLoaded = (file: string) => setLoadedImages(current => ({ ...current, [file]: true }));
    const markImageAsPending = (file: string) => setLoadedImages(current => {
        if (current[file] === false) {
            return current;
        }
        return { ...current, [file]: false };
    });

    const copyMediaAddress = async (file: string) => {
        const address = getMediaAddress(file) || '';
        if (!address) {
            setCopySnackMessage('Unable to resolve media URL');
            setCopySnackOpen(true);
            return;
        }

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(address);
                setCopySnackMessage('Media address copied');
            } else {
                window.prompt('Copy media address', address);
                setCopySnackMessage('Media address ready to copy');
            }
        } catch {
            window.prompt('Copy media address', address);
            setCopySnackMessage('Media address ready to copy');
        }

        setCopySnackOpen(true);
    };

    const renderCopyButton = (file: string) => (
        <IconButton
            aria-label="Copy media address"
            onClick={(event) => {
                event.stopPropagation();
                copyMediaAddress(file);
            }}
            sx={{
                position: 'fixed',
                top: 12,
                left: 12,
                zIndex: 3,
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.35)',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.55)' },
            }}
        >
            <ContentCopyIcon />
        </IconButton>
    );

    return (
        <>
            {imageCover && (() => {
                const thumbUrl = getThumbAddress(imageCover.file);
                const isLoaded = isImageLoaded(imageCover.file);
                const count = imageMedias.length;
                // Paper under the cover, one sheet per extra image (two at most):
                // the others are a swipe away in the slider, not more thumbnails.
                const sheets = Math.min(count - 1, 2);
                const sheetShadow = Array.from({ length: sheets }, (_, i) => {
                    const offset = (i + 1) * 3;
                    return `${offset}px ${offset}px 0 -1px #fff, ${offset}px ${offset}px 0 0 rgba(0, 0, 0, 0.28)`;
                }).join(', ');
                const label = withLabel
                    ? anyOcr ? 'Lire l’acte' : count > 1 ? `${count} documents` : 'Voir l’acte'
                    : null;
                return <Box
                    component="button"
                    type="button"
                    onClick={() => openFullscreenMedia(imageCover)}
                    aria-label={anyOcr ? 'Lire l’acte et sa transcription' : 'Voir les documents'}
                    title={count > 1 ? `${count} documents, à faire défiler` : undefined}
                    sx={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                        ml: '10px',
                        mt: '5px',
                        mr: `${sheets * 3}px`,
                        p: 0,
                        border: 0,
                        background: 'none',
                        cursor: 'pointer',
                        font: 'inherit',
                        verticalAlign: 'top',
                        '&:hover .act-cover, &:focus-visible .act-cover': { transform: 'translateY(-2px) rotate(-1deg)' },
                        '&:hover .act-label, &:focus-visible .act-label': { textDecoration: 'underline' },
                    }}
                >
                    <Box
                        className="act-cover"
                        sx={{
                            position: 'relative',
                            width: thumbPx,
                            height: thumbPx,
                            borderRadius: '3px',
                            backgroundColor: '#f6f1e7',
                            boxShadow: `${sheetShadow ? `${sheetShadow}, ` : ''}0 1px 3px rgba(0, 0, 0, 0.3)`,
                            transition: 'transform 0.15s ease',
                        }}
                    >
                        <Box sx={{ width: '100%', height: '100%', overflow: 'hidden', borderRadius: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {!isLoaded ? (
                                <ImageIcon sx={{ fontSize: thumbPx, width: '100%', height: '100%', color: 'text.secondary', opacity: 0.45 }} />
                            ) : null}
                            {thumbUrl ? (
                                <img
                                    src={thumbUrl}
                                    alt=""
                                    width={thumbPx}
                                    height={thumbPx}
                                    onLoad={() => markImageAsLoaded(imageCover.file)}
                                    onError={() => markImageAsPending(imageCover.file)}
                                    style={{ display: isLoaded ? 'block' : 'none', width: thumbPx, height: thumbPx, objectFit: 'cover', filter: 'sepia(0.25)' }}
                                />
                            ) : null}
                        </Box>
                        {count > 1 ? (
                            <Box component="span" sx={{
                                position: 'absolute', top: -6, right: -6 - sheets * 3, minWidth: 18, height: 18, px: '4px',
                                borderRadius: '9px', bgcolor: 'primary.main', color: 'primary.contrastText',
                                fontSize: 11, fontWeight: 700, lineHeight: '18px', textAlign: 'center',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                            }}>{count}</Box>
                        ) : null}
                        {anyOcr && !label ? (
                            <SubjectIcon
                                titleAccess="Transcription disponible"
                                sx={{
                                    position: 'absolute', right: 1, bottom: 1,
                                    fontSize: Math.max(12, Math.round(thumbPx / 4)),
                                    color: 'white', bgcolor: 'rgba(0, 0, 0, 0.55)', borderRadius: '3px', pointerEvents: 'none',
                                }}
                            />
                        ) : null}
                    </Box>
                    {label ? (
                        <Typography component="span" className="act-label" sx={{
                            display: 'inline-flex', alignItems: 'center', gap: '2px',
                            fontSize: 11, fontWeight: 700, lineHeight: 1.1, color: 'primary.main', whiteSpace: 'nowrap',
                        }}>
                            {anyOcr ? <SubjectIcon sx={{ fontSize: 13 }} /> : null}{label}
                        </Typography>
                    ) : null}
                </Box>;
            })()}

            {pdfMedias.map((item, index) => (
                <Box
                    key={`${item.handle || item.file}:pdf:${index}`}
                    onClick={() => openFullscreenMedia(item)}
                >
                    {getPdfThumbAddress(item.file) ? (
                        <img
                            src={getPdfThumbAddress(item.file)}
                            alt="cliquez pour voir les documents"
                            width={thumbPx}
                            height={thumbPx}
                            style={{ marginLeft: '5px', marginTop: '5px', padding: '2px', cursor: 'pointer', border: '1px solid blue', objectFit: 'cover' }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: thumbPx,
                                height: thumbPx,
                                marginLeft: '5px',
                                marginTop: '5px',
                                border: '1px solid blue',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 11,
                                color: 'text.secondary',
                                cursor: 'pointer',
                                userSelect: 'none',
                            }}
                        >
                            PDF
                        </Box>
                    )}
                </Box>
            ))}

            {audioMedias.map((item, index) => (
                <Box
                    key={`${item.handle || item.file}:audio:${index}`}
                    onClick={() => openFullscreenMedia(item)}
                    sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', marginLeft: '5px', marginTop: '5px', padding: '4px', cursor: 'pointer', border: '1px solid blue', borderRadius: '4px' }}
                >
                    <AudioFileIcon sx={{ fontSize: 34 }} />
                    <Typography variant="caption" sx={{ maxWidth: 90, textAlign: 'center', lineHeight: 1.1 }}>
                        audio
                    </Typography>
                </Box>
            ))}

            {videoMedias.map((item, index) => (
                <Box
                    key={`${item.handle || item.file}:video:${index}`}
                    onClick={() => openFullscreenMedia(item)}
                    sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', marginLeft: '5px', marginTop: '5px', padding: '4px', cursor: 'pointer', border: '1px solid blue', borderRadius: '4px' }}
                >
                    {getVideoPosterAddress(item.file) ? (
                        <img src={getVideoPosterAddress(item.file)} alt="apercu video" width={thumbPx} height={thumbPx} style={{ marginBottom: 4, objectFit: 'cover' }} />
                    ) : null}
                    <VideoFileIcon sx={{ fontSize: 30 }} />
                    <Typography variant="caption" sx={{ maxWidth: 90, textAlign: 'center', lineHeight: 1.1 }}>
                        video
                    </Typography>
                </Box>
            ))}

            <Dialog
                open={Boolean(dialogMedia)}
                onClose={closeFullscreenMedia}
                maxWidth={false}
                slotProps={{
                    paper: {
                        sx: {
                            m: 0,
                            width: '100vw',
                            height: '100vh',
                            maxWidth: '100vw',
                            maxHeight: '100vh',
                            backgroundColor: 'rgba(0,0,0,0.85)',
                            boxShadow: 'none',
                            borderRadius: 0,
                            overflow: 'hidden',
                        },
                    },
                }}
            >
                <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                    <IconButton
                        onClick={closeFullscreenMedia}
                        sx={{ position: 'absolute', top: 10, right: 10, zIndex: 2, color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {dialogMedia && renderCopyButton(dialogMedia.file)}
                    {dialogMedia && (
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {dialogMedia.kind === 'audio' ? (
                                <audio
                                    controls
                                    autoPlay
                                    preload="metadata"
                                    src={getMediaAddress(dialogMedia.file)}
                                    style={{ width: 'min(88vw, 720px)' }}
                                />
                            ) : dialogMedia.kind === 'video' ? (
                                <video
                                    controls
                                    autoPlay
                                    playsInline
                                    preload="metadata"
                                    poster={getVideoPosterAddress(dialogMedia.file)}
                                    src={getMediaAddress(dialogMedia.file)}
                                    style={{ maxWidth: 'min(92vw, 1200px)', maxHeight: '90vh', width: 'auto', height: 'auto' }}
                                />
                            ) : (
                                <PdfViewer key={dialogMedia.file} file={getMediaAddress(dialogMedia.file) || ''} />
                            )}
                        </Box>
                    )}
                </Box>
            </Dialog>

            <PhotoSlider
                images={photoSliderImages}
                index={activeImageIndex >= 0 ? activeImageIndex : 0}
                visible={activeImageIndex >= 0}
                onClose={closeFullscreenMedia}
                onIndexChange={handlePhotoSliderIndexChange}
                toolbarRender={() => (
                    <Tooltip title={showOcr ? 'Masquer la transcription' : 'Afficher la transcription'}>
                        <IconButton
                            aria-label="Afficher ou masquer la transcription"
                            onClick={() => setShowOcr((value) => !value)}
                            sx={{ color: showOcr ? 'white' : 'grey.500' }}
                        >
                            <SubjectIcon />
                        </IconButton>
                    </Tooltip>
                )}
                overlayRender={() => (showOcr && sliderHandle
                    ? <OcrPanel acts={ocrActs} loading={ocrQuery.isLoading} activeZone={activeZone} onHoverZone={setActiveZone} isMobile={isMobile} onClose={() => setShowOcr(false)} />
                    : null)}
            />

            <Snackbar
                open={copySnackOpen}
                autoHideDuration={1800}
                onClose={() => setCopySnackOpen(false)}
                message={copySnackMessage}
            />
        </>
    );
}
